import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { RootState } from "../../../../../store/Index";
import {
  MemoryBook,
  setSelectedBook,
} from "../../../../../store/slice/MemoryBookSlice";
import { fetchAssignedMemoryBooks } from "./service/memoryBook.api";
import { mapAssignmentToMemoryBook } from "./mapAssignmentToMemoryBook";

interface MemoryBookAssignmentsTabsProps {
  onSwitchBook?: (book: MemoryBook) => void;
}

const AssignmentCard: React.FC<{
  book: MemoryBook;
  isSelected: boolean;
  onSelect: () => void;
  actionLabel: string;
}> = ({ book, isSelected, onSelect, actionLabel }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const physicalDone = book.physical_comments - book.unfinished_comments;
  const onlineDone = book.online_comments - book.unfinished_online_comments;

  return (
    <Box
      sx={{
        minWidth: 220,
        flexShrink: 0,
        p: 2,
        borderRadius: 3,
        border: isSelected
          ? `2px solid ${reduxTheme.primary_colors?.[400] || "#614438"}`
          : `2px solid ${reduxTheme.primary_colors?.[500] || "#614438"}`,
        backgroundColor: reduxTheme.neutral_colors?.[900] || "#f7f5f2",
        transition: "all 0.2s ease",
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        <Avatar
          src={book.thumbnail || "./placeholder.svg"}
          sx={{ width: 56, height: 56 }}
        />
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.2 }}
        >
          {book.title}
        </Typography>
      </Box>

      <Box
        mt={1.25}
        display="flex"
        justifyContent="space-between"
        sx={{
          borderRadius: 3,
          px: 2,
          py: 0.75,
          backgroundColor: "rgba(0,0,0,0.03)",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
          Physical:{" "}
          <strong>
            {physicalDone}/{book.physical_comments}
          </strong>
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
          Online:{" "}
          <strong>
            {onlineDone}/{book.online_comments}
          </strong>
        </Typography>
      </Box>

      <Button
        fullWidth
        size="small"
        variant={isSelected ? "outlined" : "contained"}
        disabled={isSelected}
        onClick={onSelect}
        sx={{
          mt: 1.25,
          py: 0.6,
          fontSize: "0.75rem",
          borderRadius: 3,
          textTransform: "none",
        }}
      >
        {isSelected ? "Currently Active" : actionLabel}
      </Button>
    </Box>
  );
};

const MemoryBookAssignmentsTabs: React.FC<MemoryBookAssignmentsTabsProps> = ({
  onSwitchBook,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { selectedBook } = useSelector((state: RootState) => state.memoryBook);

  const [tab, setTab] = useState<"current" | "completed">("current");
  const [books, setBooks] = useState<MemoryBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBooks = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAssignedMemoryBooks(user.id);
      const results = res.data?.results ?? [];
      setBooks(results.map(mapAssignmentToMemoryBook));
    } catch {
      setError("Failed to load assignments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const currentBooks = books.filter((b) => b.status !== "completed");
  const completedBooks = books.filter((b) => b.status === "completed");
  const visibleBooks = tab === "current" ? currentBooks : completedBooks;

  const handleSwitchTo = (book: MemoryBook) => {
    if (book.id === selectedBook?.id) return;
    dispatch(setSelectedBook(book));
    onSwitchBook?.(book);
  };

  const handleView = (book: MemoryBook) => {
    navigate(`/memory-table/view/${book.id}`);
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: "none" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{
            minHeight: "auto",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: "auto",
              py: 1,
            },
          }}
        >
          <Tab
            label={`Current Assignments${currentBooks.length ? ` (${currentBooks.length})` : ""}`}
            value="current"
          />
          <Tab
            label={`Completed${completedBooks.length ? ` (${completedBooks.length})` : ""}`}
            value="completed"
          />
        </Tabs>

        <Tooltip title="Refresh">
          <span>
            <Button
              size="small"
              onClick={loadBooks}
              disabled={loading}
              sx={{ minWidth: "auto", p: 1 }}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh fontSize="small" />
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {!error && !loading && visibleBooks.length === 0 && (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ py: 3, textAlign: "center" }}
        >
          {tab === "current"
            ? "No other assignments right now."
            : "No completed memory books yet."}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          py: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {visibleBooks.map((book) => (
          <AssignmentCard
            key={book.assignmentId ?? book.id}
            book={book}
            isSelected={tab === "current" && book.id === selectedBook?.id}
            onSelect={() =>
              tab === "completed" ? handleView(book) : handleSwitchTo(book)
            }
            actionLabel={tab === "completed" ? "View" : "Switch To"}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default MemoryBookAssignmentsTabs;
